import type { TFunction } from "i18next";
import {
  auditActionCategory,
  shortenAuditId,
  buildAuditSummaryEn,
  type AuditCategory,
} from "@shared/audit-log-meta";

export type AuditTableRow = {
  id: string;
  user_email: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: unknown;
  ip: string | null;
  created_at: string;
  summary?: string | null;
  action_category?: string | null;
};

export type { AuditCategory };

export const AUDIT_CATEGORY_CHIPS: { id: AuditCategory | "all"; labelKey: string }[] = [
  { id: "all", labelKey: "settings.auditCategoryAll" },
  { id: "crm", labelKey: "settings.auditCategoryCrm" },
  { id: "images", labelKey: "settings.auditCategoryImages" },
  { id: "admin", labelKey: "settings.auditCategoryAdmin" },
  { id: "social", labelKey: "settings.auditCategorySocial" },
  { id: "auth", labelKey: "settings.auditCategoryAuth" },
  { id: "other", labelKey: "settings.auditCategoryOther" },
];

function actionI18nKey(action: string): string {
  return `settings.auditAction.${action.replace(/\./g, "_")}`;
}

export function getAuditActionLabel(t: TFunction, action: string): string {
  const key = actionI18nKey(action);
  const label = t(key);
  if (label !== key) return label;
  return action;
}

function asRecord(v: unknown): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  return v as Record<string, unknown>;
}

export function getPayloadMeta(payload: unknown): Record<string, unknown> {
  const p = asRecord(payload);
  return asRecord(p.meta);
}

export function getAuditPayloadChanged(
  payload: unknown
): Array<{ field: string; from?: unknown; to?: unknown }> | undefined {
  const p = asRecord(payload);
  const ch = p.changed;
  if (!Array.isArray(ch)) return undefined;
  const out: Array<{ field: string; from?: unknown; to?: unknown }> = [];
  for (const item of ch) {
    const row = asRecord(item);
    const field = typeof row.field === "string" ? row.field : "";
    if (!field) continue;
    out.push({ field, from: row.from, to: row.to });
  }
  return out.length ? out : undefined;
}

/** Actions where a structured before/after list is shown when `payload.changed` exists. */
export const AUDIT_ACTIONS_WITH_CHANGE_UI = new Set([
  "settings_update",
  "crm.deal.updated",
  "admin.user.updated",
]);

export function shouldShowAuditChangeList(action: string, payload: unknown): boolean {
  if (!AUDIT_ACTIONS_WITH_CHANGE_UI.has(action)) return false;
  const ch = getAuditPayloadChanged(payload);
  return Array.isArray(ch) && ch.length > 0;
}

export function formatAuditDisplayValue(t: TFunction, raw: string): string {
  const s = raw.trim();
  if (s === "true") return t("settings.auditBoolTrue");
  if (s === "false") return t("settings.auditBoolFalse");
  if (s === "—" || s === "-" || s === "") return t("settings.auditDash");
  return raw;
}

export function getAuditChangeFieldLabel(t: TFunction, action: string, field: string): string {
  if (action === "settings_update") {
    return t(`settings.auditSettingKey.${field}`, { defaultValue: field });
  }
  if (action === "crm.deal.updated") {
    return t(`settings.auditCrmDealField.${field}`, { defaultValue: field });
  }
  if (action === "admin.user.updated") {
    return t(`settings.auditAdminUserField.${field}`, { defaultValue: field });
  }
  return field;
}

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return String(v);
}

export function getAuditResultFlags(payload: unknown): { failed: boolean; reason?: string } {
  const p = asRecord(payload);
  if (p.result === "failed") {
    return { failed: true, reason: typeof p.reason === "string" ? p.reason : undefined };
  }
  return { failed: false };
}

function dealLabel(meta: Record<string, unknown>, entityId: string | null): string {
  const title = str(meta.dealTitle ?? meta.title).trim();
  if (title) return title;
  const did = str(meta.dealId).trim();
  if (did) return shortenAuditId(did, 8);
  if (entityId) return shortenAuditId(entityId, 8);
  return "";
}

/** Resolved category: prefers DB column when present (export), else derived from action. */
export function getRowCategory(row: AuditTableRow): AuditCategory {
  const fromDb = row.action_category as AuditCategory | null | undefined;
  if (fromDb === "crm" || fromDb === "images" || fromDb === "admin" || fromDb === "social" || fromDb === "auth" || fromDb === "other") {
    return fromDb;
  }
  return auditActionCategory(row.action);
}

export function getAuditSummaryLocalized(t: TFunction, entry: AuditTableRow): string {
  const meta = getPayloadMeta(entry.payload);
  const changed = getAuditPayloadChanged(entry.payload);
  const eid = entry.entity_id || "";
  const action = entry.action;

  const deal = dealLabel(meta, action === "crm.deal.stage_changed" || action === "crm.deal.updated" ? eid : null);
  const { failed, reason } = getAuditResultFlags(entry.payload);

  let summary: string;
  switch (action) {
    case "crm.deal.stage_changed":
      summary = t("settings.auditSummary.stageChanged", {
        deal: deal || t("settings.auditSummary.dealFallback"),
        from: str(meta.fromStage) || "—",
        to: str(meta.toStage) || "—",
      });
      break;
    case "crm.deal.updated": {
      if (Array.isArray(changed) && changed.length > 0) {
        const parts = changed
          .slice(0, 2)
          .map((c) => `${c.field}: ${shortenAuditId(str(c.from), 12)}→${shortenAuditId(str(c.to), 12)}`);
        summary = t("settings.auditSummary.dealUpdatedFields", {
          deal: deal || shortenAuditId(eid, 8),
          fields: parts.join("; ") + (changed.length > 2 ? "…" : ""),
        });
      } else {
        summary = t("settings.auditSummary.dealUpdated", {
          deal: deal || shortenAuditId(eid, 8),
        });
      }
      break;
    }
    case "crm.file.uploaded": {
      const head = str(meta.dealTitle ?? meta.title).trim() || (str(meta.dealId) ? shortenAuditId(str(meta.dealId), 8) : shortenAuditId(eid, 8));
      summary = t("settings.auditSummary.fileUploaded", {
        deal: head || "—",
        file: str(meta.fileName) || "—",
      });
      break;
    }
    case "crm.contact.created":
      summary = str(meta.name)
        ? t("settings.auditSummary.contactCreated", { name: str(meta.name) })
        : t("settings.auditSummary.contactCreatedGeneric");
      break;
    case "crm.contact.updated":
      summary = t("settings.auditSummary.contactUpdated", { id: shortenAuditId(eid, 8) });
      break;
    case "crm.contact.deleted": {
      const nm = str(meta.name).trim();
      summary = nm
        ? t("settings.auditSummary.contactDeleted", { name: nm })
        : t("settings.auditSummary.contactDeletedGeneric", { id: shortenAuditId(eid, 8) });
      break;
    }
    case "crm.deal.created":
      summary = t("settings.auditSummary.dealCreated", {
        title: str(meta.title ?? meta.dealTitle).trim() || shortenAuditId(eid, 8),
      });
      break;
    case "crm.note.created":
    case "crm.note.updated":
    case "crm.note.deleted": {
      const dh =
        str(meta.dealTitle).trim() ||
        (str(meta.dealId) ? shortenAuditId(str(meta.dealId), 8) : "");
      summary = t("settings.auditSummary.noteDeal", {
        deal: dh || "—",
        noteId: shortenAuditId(eid, 8),
      });
      break;
    }
    case "crm.document.linked":
      summary = t("settings.auditSummary.documentLinked", {
        deal: dealLabel(meta, null) || shortenAuditId(str(meta.dealId), 8),
        sf: shortenAuditId(str(meta.sfDocumentId), 8),
      });
      break;
    case "crm.document.unlinked":
      summary = t("settings.auditSummary.documentUnlinked", {
        deal: dealLabel(meta, null) || shortenAuditId(str(meta.dealId), 8),
        doc: shortenAuditId(eid, 8),
      });
      break;
    case "crm.deal_company.linked":
    case "crm.deal_company.unlinked": {
      const cmp =
        str(meta.companyName) ||
        (str(meta.ico) ? `${t("settings.auditSummary.ico")} ${str(meta.ico)}` : shortenAuditId(str(meta.contactId), 8));
      summary = t("settings.auditSummary.dealCompany", {
        deal: dealLabel(meta, null) || shortenAuditId(str(meta.dealId), 8),
        company: cmp || "—",
      });
      break;
    }
    case "social.asset.approved":
    case "social.asset.generated":
      summary = t("settings.auditSummary.socialAsset", {
        variant: str(meta.variant) || "—",
        ref: shortenAuditId(str(meta.sourcePhotoId), 8),
      });
      break;
    case "social.post.published":
      summary = t("settings.auditSummary.socialPost", {
        platform: str(meta.provider) || "—",
        ref: shortenAuditId(eid, 8),
      });
      break;
    case "social.integration.meta.updated":
      summary = t("settings.auditSummary.socialIntegrationMeta");
      break;
    case "images.folder.created":
    case "images.folder.updated":
    case "images.folder.deleted":
      summary = t("settings.auditSummary.imageFolder", {
        name: str(meta.folderName) || shortenAuditId(str(meta.folderId), 8),
      });
      break;
    case "images.photo.uploaded":
    case "images.photo.reordered":
    case "images.photo.moved":
    case "images.photo.deleted":
    case "images.photo.batch_deleted":
      summary = t("settings.auditSummary.imagePhoto", {
        folder: shortenAuditId(str(meta.folderId), 8),
      });
      break;
    case "images.import.drive_started":
      summary = t("settings.auditSummary.imagesImport", {
        folder: shortenAuditId(str(meta.folderId), 8),
      });
      break;
    case "admin.user.invited":
    case "admin.user.updated":
      summary =
        str(meta.email) || str(meta.invitedEmail) || str(meta.targetEmail) || shortenAuditId(eid, 8);
      break;
    case "user_delete":
      summary = str(meta.email) || str(meta.deletedEmail) || shortenAuditId(eid, 8);
      break;
    case "user.account.deleted":
      summary = t("settings.auditSummary.accountDeletedSelf");
      break;
    case "settings_update": {
      if (Array.isArray(changed) && changed.length > 0) {
        const parts = changed.slice(0, 6).map((c) => {
          const lab = t(`settings.auditSettingKey.${c.field}`, { defaultValue: c.field });
          return `${lab}: ${str(c.from)} → ${str(c.to)}`;
        });
        summary =
          parts.join("; ") + (changed.length > 6 ? ` (+${changed.length - 6})` : "");
      } else {
        const tried = meta.patchKeysTried;
        if (Array.isArray(tried) && tried.length > 0) {
          const labels = tried
            .map((k) =>
              t(`settings.auditSettingKey.${String(k)}`, { defaultValue: String(k) })
            )
            .join(", ");
          summary = t("settings.auditSummary.settingsNoValueChange", { keys: labels });
        } else {
          summary = t("settings.auditSummary.settingsUpdated");
        }
      }
      break;
    }
    case "auth.password_changed":
      summary = t("settings.auditSummary.passwordChanged");
      break;
    case "auth.sessions_revoked_others":
      summary = t("settings.auditSummary.sessionsRevokedOthers");
      break;
    case "generate_advance":
      summary = t("settings.auditSummary.generateAdvance", {
        deal: dealLabel(meta, null) || shortenAuditId(str(meta.dealId), 8),
      });
      break;
    case "crm.deal.batch_deleted":
    case "crm.deal.batch_moved":
    case "crm.deal.batch_owner_changed":
      summary = t("settings.auditSummary.batchDeals", {
        ok: str(meta.success),
        fail: str(meta.failed),
        total: str(meta.requestedCount),
      });
      break;
    default:
      summary = buildAuditSummaryEn(action, {
        meta,
        changed,
        entityType: entry.entity_type,
        entityId: entry.entity_id,
      });
  }

  if (failed) {
    return reason
      ? t("settings.auditSummary.failedWithReason", { summary, reason })
      : t("settings.auditSummary.failedOnly", { summary });
  }
  return summary;
}

export function getAuditDeepLink(entry: AuditTableRow): string | null {
  const meta = getPayloadMeta(entry.payload);
  const dealId =
    entry.entity_type === "deal" && entry.entity_id
      ? entry.entity_id
      : str(meta.dealId).trim() || null;
  if (dealId && /^[0-9a-f-]{36}$/i.test(dealId)) {
    return `/crm/deals/${dealId}`;
  }
  return null;
}

export function getAuditTechnicalSecondLine(entry: AuditTableRow): string {
  const p = asRecord(entry.payload);
  const rid = typeof p.requestId === "string" ? p.requestId : "";
  const parts = [entry.action, entry.entity_type && entry.entity_id ? `${entry.entity_type}:${shortenAuditId(entry.entity_id, 10)}` : ""];
  if (rid) parts.push(`req ${shortenAuditId(rid, 12)}`);
  return parts.filter(Boolean).join(" · ");
}
